import i18n from '../i18n/config';
import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Alert,
    Tab,
    Tabs
} from '@mui/material';
import {
    Receipt as ReceiptIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    Payment as PaymentIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    PlayArrow as GenerateIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiCall } from '../hooks/useApi';
import { api } from '../services/api';
import LoadingPage from '../components/common/LoadingPage';
import { toast } from 'react-toastify';

const safeText = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return fallback;
    }
};

const safeNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const BillingInvoicesPage = () => {
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);
    const [invoiceDialog, setInvoiceDialog] = useState({ open: false, invoice: null });
    const [generateDialog, setGenerateDialog] = useState({ open: false });
    const [paymentDialog, setPaymentDialog] = useState({ open: false, invoice: null });

    // Check access
    const {
        data: accessCheck,
        loading: accessLoading
    } = useApiCall(() => api.billing.checkAccess());

    // Load invoices
    const {
        data: invoices,
        loading: invoicesLoading,
        refetch: refetchInvoices
    } = useApiCall(() => api.billing.listInvoices());

    // Load stats
    const {
        data: stats,
        loading: statsLoading,
        refetch: refetchStats
    } = useApiCall(() => api.billing.getStats());

    const handleGenerateMonthly = async () => {
        try {
            const result = await api.billing.generateMonthlyInvoices();
            toast.success(t('hardcoded.pages_billinginvoicespage.generated_count_facturas_generadas_exitosamente', { generated_count: result.generated_count }));
            setGenerateDialog({ open: false });
            refetchInvoices();
            refetchStats();
        } catch (error) {
            toast.error(t('hardcoded.pages_billinginvoicespage.error_al_generar_facturas') + (error.response?.data?.detail || error.message));
        }
    };

    const handleRecordPayment = async (invoiceId, paymentData) => {
        try {
            await api.billing.recordPayment(invoiceId, paymentData);
            toast.success(t('hardcoded.pages_billinginvoicespage.pago_registrado_exitosamente'));
            setPaymentDialog({ open: false, invoice: null });
            refetchInvoices();
            refetchStats();
        } catch (error) {
            toast.error(t('hardcoded.pages_billinginvoicespage.error_al_registrar_pago') + (error.response?.data?.detail || error.message));
        }
    };

    if (accessLoading || invoicesLoading || statsLoading) return <LoadingPage />;

    if (!accessCheck?.has_access) {
        return (
            <Container maxWidth={false} disableGutters>
                <Alert severity="error">
                    {t('hardcoded.pages_billinginvoicespage.acceso_denegado_esta_funcionalidad_es')}</Alert>
            </Container>
        );
    }

    const getStatusColor = (status) => {
        const colors = {
            draft: 'default',
            pending: 'warning',
            paid: 'success',
            overdue: 'error',
            cancelled: 'default',
            refunded: 'info'
        };
        return colors[status] || 'default';
    };

    const getStatusLabel = (status) => {
        const labels = {
            draft: t('hardcoded.pages_billinginvoicespage.borrador'),
            pending: t('hardcoded.components_admin_ordermanagement.pendiente'),
            paid: t('hardcoded.pages_billinginvoicespage.pagada'),
            overdue: t('hardcoded.pages_billinginvoicespage.vencida'),
            cancelled: t('hardcoded.components_admin_ordermanagement.cancelada'),
            refunded: t('hardcoded.pages_billinginvoicespage.reembolsada')
        };
        return labels[status] || status;
    };

    const filterInvoices = (invoices, tab) => {
        if (!invoices) return [];

        switch (tab) {
            case 0: // Todas
                return invoices;
            case 1: // Pendientes
                return invoices.filter(inv => inv.status === 'pending');
            case 2: // Pagadas
                return invoices.filter(inv => inv.status === 'paid');
            case 3: // Vencidas
                return invoices.filter(inv => inv.status === 'overdue');
            default:
                return invoices;
        }
    };

    const filteredInvoices = filterInvoices(invoices, currentTab);

    return (
        <Container maxWidth={false} disableGutters>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">

                    {i18n.t('hardcoded.components_common_sidebar.facturas')}</Typography>
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<GenerateIcon />}
                        onClick={() => setGenerateDialog({ open: true })}
                        sx={{ mr: 2 }}
                    >
                        {i18n.t('hardcoded.pages_billinginvoicespage.generar_facturas_mensuales')}</Button>
                    <IconButton onClick={() => { refetchInvoices(); refetchStats(); }}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Stats Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {i18n.t('hardcoded.pages_billinginvoicespage.total_facturas')}</Typography>
                                <Typography variant="h4">
                                    {safeNumber(stats.total_invoices, 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {t('hardcoded.components_settings_companynetworkstab.pendientes')}</Typography>
                                <Typography variant="h4" color="warning.main">
                                    {safeNumber(stats.pending_invoices, 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {i18n.t('hardcoded.pages_billinginvoicespage.vencidas')}</Typography>
                                <Typography variant="h4" color="error.main">
                                    {safeNumber(stats.overdue_invoices, 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary" gutterBottom>
                                    {i18n.t('hardcoded.pages_billinginvoicespage.total_pendiente')}</Typography>
                                <Typography variant="h4" color="warning.main">
                                    ${safeNumber(stats.total_pending, 0).toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
                    <Tab label={t('hardcoded.components_settings_notificationpreferenceseditor.todas')} />
                    <Tab label={t('hardcoded.components_settings_companynetworkstab.pendientes')} />
                    <Tab label={t('hardcoded.pages_billinginvoicespage.pagadas')} />
                    <Tab label={t('hardcoded.pages_billinginvoicespage.vencidas')} />
                </Tabs>
            </Paper>

            {/* Invoices Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('hardcoded.pages_adminstorepage.numero')}</TableCell>
                            <TableCell>{i18n.t('navbar.company')}</TableCell>
                            <TableCell>{i18n.t('hardcoded.pages_billinginvoicespage.periodo')}</TableCell>
                            <TableCell align="right">{i18n.t('hardcoded.components_admin_couponform.subtotal')}</TableCell>
                            <TableCell align="right">{i18n.t('common.total')}</TableCell>
                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                            <TableCell>{t('hardcoded.pages_billinginvoicespage.vencimiento')}</TableCell>
                            <TableCell align="center">{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInvoices && filteredInvoices.length > 0 ? (
                            filteredInvoices.map((invoice) => (
                                <TableRow key={invoice.id}>
                                    <TableCell>{safeText(invoice.invoice_number, t('hardcoded.pages_billinginvoicespage.no_disponible'))}</TableCell>
                                    <TableCell>
                                        {safeText(invoice.company_name, t('hardcoded.pages_billinginvoicespage.no_disponible'))}
                                    </TableCell>
                                    <TableCell>
                                        {safeText(invoice.period_start)} - {safeText(invoice.period_end)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {safeText(invoice.currency, 'USD')} {safeNumber(invoice.subtotal, 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{safeText(invoice.currency, 'USD')} {safeNumber(invoice.total, 0).toFixed(2)}</strong>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={safeText(getStatusLabel(invoice.status), t('hardcoded.pages_billinginvoicespage.no_disponible'))}
                                            color={getStatusColor(invoice.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {safeText(invoice.due_date, '-')}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={t('hardcoded.components_alarmprofiles_profilecard.ver_detalle')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setInvoiceDialog({ open: true, invoice })}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {invoice.status !== 'paid' && (
                                            <Tooltip title={t('hardcoded.pages_billinginvoicespage.registrar_pago')}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => setPaymentDialog({ open: true, invoice })}
                                                >
                                                    <PaymentIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {t('hardcoded.pages_billinginvoicespage.no_hay_facturas_para_mostrar')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Generate Dialog */}
            <Dialog open={generateDialog.open} onClose={() => setGenerateDialog({ open: false })}>
                <DialogTitle>{i18n.t('hardcoded.pages_billinginvoicespage.generar_facturas_mensuales')}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('hardcoded.pages_billinginvoicespage.se_generaran_facturas_automaticamente_para')}</Alert>
                    <Typography>
                        {t('hardcoded.pages_billinginvoicespage.desea_continuar_con_la_generacion')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenerateDialog({ open: false })}>
                        {t('common.cancel')}</Button>
                    <Button onClick={handleGenerateMonthly} variant="contained">
                        {i18n.t('hardcoded.components_admin_couponform.generar')}</Button>
                </DialogActions>
            </Dialog>

            {/* Invoice Detail Dialog */}
            <InvoiceDetailDialog
                open={invoiceDialog.open}
                invoice={invoiceDialog.invoice}
                onClose={() => setInvoiceDialog({ open: false, invoice: null })}
            />

            {/* Payment Dialog */}
            <PaymentDialog
                open={paymentDialog.open}
                invoice={paymentDialog.invoice}
                onClose={() => setPaymentDialog({ open: false, invoice: null })}
                onSave={handleRecordPayment}
            />
        </Container>
    );
};

// Invoice Detail Dialog
const InvoiceDetailDialog = ({ open, invoice, onClose }) => {
  const { t } = useTranslation();
    if (!invoice) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('hardcoded.pages_billinginvoicespage.detalle_de_factura_-')}{invoice.invoice_number}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.pages_billinginvoicespage.periodo')}</Typography>
                        <Typography>
                            {safeText(invoice.period_start)} - {safeText(invoice.period_end)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('gps.table.status')}</Typography>
                        <Typography>
                            {safeText(invoice.status?.toUpperCase(), '-')}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('hardcoded.components_admin_couponform.subtotal')}</Typography>
                        <Typography>
                            {safeText(invoice.currency, 'USD')} {safeNumber(invoice.subtotal, 0).toFixed(2)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('common.total')}</Typography>
                        <Typography variant="h6">
                            {safeText(invoice.currency, 'USD')} {safeNumber(invoice.total, 0).toFixed(2)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.pages_billinginvoicespage.fecha_de_vencimiento')}</Typography>
                        <Typography>
                            {safeText(invoice.due_date, '-')}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.pages_billinginvoicespage.fecha_de_pago')}</Typography>
                        <Typography>
                            {safeText(invoice.paid_date, '-')}
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

// Payment Dialog
const PaymentDialog = ({ open, invoice, onClose, onSave }) => {
  const { t } = useTranslation();
    const [formData, setFormData] = useState({
        amount: 0,
        payment_method: 'transfer',
        payment_reference: '',
        notes: '',
        is_confirmed: true
    });

    React.useEffect(() => {
        if (invoice) {
            setFormData({
                amount: safeNumber(invoice.total, 0),
                payment_method: 'transfer',
                payment_reference: '',
                notes: '',
                is_confirmed: true
            });
        }
    }, [invoice]);

    const handleSubmit = () => {
        onSave(invoice.id, formData);
    };

    if (!invoice) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {i18n.t('hardcoded.pages_billinginvoicespage.registrar_pago_-')}{invoice.invoice_number}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billinginvoicespage.monto')}
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                            InputProps={{
                                startAdornment: <Typography sx={{ mr: 1 }}>{invoice.currency}</Typography>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label={t('hardcoded.pages_billinginvoicespage.metodo_de_pago')}
                            value={formData.payment_method}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                        >
                            <MenuItem value="transfer">{i18n.t('hardcoded.pages_billinginvoicespage.transferencia')}</MenuItem>
                            <MenuItem value="card">{i18n.t('hardcoded.pages_billinginvoicespage.tarjeta')}</MenuItem>
                            <MenuItem value="cash">{i18n.t('hardcoded.pages_billinginvoicespage.efectivo')}</MenuItem>
                            <MenuItem value="check">{t('pages_billinginvoicespage.payment_check')}</MenuItem>
                            <MenuItem value="paypal">{i18n.t('hardcoded.pages_billinginvoicespage.paypal')}</MenuItem>
                            <MenuItem value="stripe">{t('pages_billinginvoicespage.payment_stripe')}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('hardcoded.pages_billinginvoicespage.referencia_de_pago')}
                            value={formData.payment_reference}
                            onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t('hardcoded.components_admin_ordermanagement.notas')}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {i18n.t('hardcoded.pages_billinginvoicespage.registrar_pago')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BillingInvoicesPage;
